// ✅ FileUploadStatus.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Heading,
  Progress,
  Button,
  Spinner,
  Center,
  List,
  ListItem,
} from "@chakra-ui/react";
import { CheckCircleIcon, DownloadIcon } from "@chakra-ui/icons";
import { getSignalRConnection } from "../config/signalRConnection";
import { HubConnectionState } from "@microsoft/signalr";

interface InvalidRow {
  Id: number;
  InvalidReason: string;
}

interface UploadResult {
  statusCode: number;
  message: string;
  data: {
    recordsInserted: number;
    invalidRows: InvalidRow[];
  };
}

interface MessageStage {
  message: string;
  stage: number;
}

interface FileUploadStatusProps {
  userId: string;
  connectionId: string;
  tableName: string;
  file: File;
}
const Base_apiUrl=process.env.REACT_APP_API_Base_Url;
const STAGES = [
  "Compressing file",
  "Upload File to Azure Blob Storage",
  "Uploaded file is a ZIP. Processing it",
  "Cleaning and validating data",
  "Uploading relevant information to database",
  "Upload Success",
];

const FileUploadStatus: React.FC<FileUploadStatusProps> = ({
  userId,
  connectionId,
  tableName,
  file,
}) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [stageMessage, setStageMessage] = useState("");
  const [signalRErrors, setSignalRErrors] = useState<InvalidRow[]>([]);
  const [processCompleted, setProcessCompleted] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

  useEffect(() => {
    const uploadFile = async () => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("tableName", tableName);
      formData.append("Connectionid", connectionId);

      try {
        const response = await fetch(
          `${Base_apiUrl}FileAction/fileUpload`,
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await response.json();
        setUploadResult(data);
      } catch (err) {
        console.error("❌ File upload failed:", err);
        setUploadResult({
          statusCode: 0,
          message: "Upload failed.",
        } as UploadResult);
      }
    };

    uploadFile();
  }, [file]);

  useEffect(() => {
    const connection = getSignalRConnection();

    const setupSignalR = async () => {
      if (connection.state !== HubConnectionState.Connected) {
        await connection.start();
      }

      connection.off("ReceiveNotification");
      connection.off("ErrorLogList");

      connection.on("ReceiveNotification", (message: MessageStage) => {
        setStageMessage(message.message);
        setCurrentStage(message.stage);
        if (message.stage === 6) setProcessCompleted(true);
      });

      connection.on("ErrorLogList", (errors: { message: InvalidRow[] }) => {
        console.log("⚠️ Received Error Log:", errors);
        setSignalRErrors(errors.message || []);
      });
    };

    setupSignalR();

    return () => {
      connection.off("ReceiveNotification");
      connection.off("ErrorLogList");
    };
  }, [userId, connectionId]);

  const handleDownloadErrors = () => {
    debugger;
    const errorRows = uploadResult?.data.invalidRows?.length
      ? uploadResult.data.invalidRows
      : signalRErrors;

    if (!errorRows.length) return;

    const errorText = errorRows
      .map((row) => `Row ${row.Id}: ${row.InvalidReason}`)
      .join("\n");

    const blob = new Blob([errorText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "upload-errors.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isFinalSuccess =
    !!uploadResult &&
    uploadResult.statusCode === 1 &&
    uploadResult.data.invalidRows.length === 0;
  const isPartialSuccess =
    !!uploadResult &&
    uploadResult.statusCode === 1 &&
    uploadResult.data.invalidRows.length > 0;
  const isError = !!uploadResult && uploadResult.statusCode !== 1;

  return (
    <Box
      maxW="lg"
      mx="auto"
      mt={2}
      p={8}
      borderWidth="1px"
      borderRadius="lg"
      boxShadow="lg"
      bg="white"
    >
      <Heading mb={6} size="lg" textAlign="center">
        File Upload Status
      </Heading>

      <VStack spacing={4} align="stretch">
        {STAGES.map((label, index) => {
          const stageIndex = index + 1;
          const isComplete = stageIndex <= stageIndex && uploadResult?.statusCode !== 0;

          return (
            <Box key={index}>
              <Text fontWeight={"medium"}>
                <CheckCircleIcon
                  color={isComplete ? "green.500" : "gray.300"}
                  mr={2}
                />
                {label}
              </Text>
              {currentStage === stageIndex && currentStage < 6 && (
                <Progress size="xs" isIndeterminate mt={1} colorScheme="blue" />
              )}
              {currentStage === stageIndex && currentStage === 6 && (
                <Progress size="xs" value={100} mt={1} colorScheme="green" />
              )}
            </Box>
          );
        })}

        {stageMessage && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            {stageMessage}
          </Alert>
        )}

        {isError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {uploadResult?.message || "Upload failed."}
          </Alert>
        )}

        {processCompleted && signalRErrors.length > 0 && (
          <Box mt={4}>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              {signalRErrors.length} invalid rows found.
            </Alert>
            <Button
              mt={2}
              leftIcon={<DownloadIcon />}
              colorScheme="red"
              variant="outline"
              onClick={handleDownloadErrors}
            >
              Download Error Log
            </Button>
          </Box>
        )}

        {isFinalSuccess && (
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            {uploadResult?.message || "File uploaded successfully!"}
            <Text mt={2}>
              Records inserted: <b>{uploadResult?.data.recordsInserted}</b>
            </Text>
          </Alert>
        )}

        {isPartialSuccess && (
          <Box>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              {uploadResult?.message || "Upload completed with some errors."}
            </Alert>
            <Box mt={3} mb={2}>
              <Text color="red.600" fontWeight="semibold">
                {uploadResult?.data.invalidRows.length} invalid rows found:
              </Text>
              <List spacing={1} maxH="150px" overflowY="auto">
                {uploadResult?.data.invalidRows.slice(0, 5).map((row, idx) => (
                  <ListItem key={idx}>
                    Row {row.Id}: {row.InvalidReason}
                  </ListItem>
                ))}
                {uploadResult?.data.invalidRows.length > 5 && (
                  <ListItem color="gray.500">
                    ...and {uploadResult.data.invalidRows.length - 5} more
                  </ListItem>
                )}
              </List>
              <Button
                mt={2}
                leftIcon={<DownloadIcon />}
                colorScheme="red"
                variant="outline"
                onClick={handleDownloadErrors}
              >
                Download Error Log
              </Button>
            </Box>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default FileUploadStatus;
